import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAgents } from "@/features/agents/hooks/use-agents";
import {
  useCallbackSlot,
  useHandleMessage,
} from "@/features/messages/hooks/use-messages";
import type { ClientMessage, MessageResolution } from "@/features/messages/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { CaretDownIcon } from "@phosphor-icons/react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";

type Outcome = Exclude<MessageResolution, "">;

/** "Now" is the next minute: the reminders run on a minute's grain, and the server refuses the past. */
const CALLBACK_NOW_DELAY_MS = 60 * 1000;

/**
 * Closing a request — shared by the dialog of a contact's file and the Messagerie's
 * own reading pane, as one button. Comments are written on the timeline
 * (`MessageTimeline`) and close nothing; this is about the *team's own list*: the
 * request comes off it, and the menu says **who takes the follow-up** — the agent
 * (now, or at the time proposed from the customer's availability), a person, or
 * nobody.
 *
 * **The call back is chosen here, at closing, and nowhere else.** Writing a public
 * comment never schedules a call: it only tells the customer if they call. Having
 * the AI call back is the one outcome that places one, to say the public comments
 * the customer has not heard yet.
 */
export function HandleMessageMenu({
  message,
  onDone,
  onOpenChange,
}: {
  message: ClientMessage;
  /** After the request was closed. */
  onDone?: (kind: "resolved") => void;
  /** For a host dialog, which must not close on the click that closes the menu. */
  onOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation("messages");
  const { format } = useDateFormat();
  const handleMessage = useHandleMessage();
  const { data: agents = [] } = useAgents();
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };

  const { data: slot, isLoading: slotLoading } = useCallbackSlot(
    message.id,
    true,
  );

  // What the call back would say: the public lines the customer has not heard yet.
  const text = (message.reply || "").trim();
  const callbackEnabled = slot?.enabled !== false && !!message.phone_number;

  // Who would place the call back: the agent that took the call, else the first one.
  const agent =
    agents.find((a) => a.id === message.agent_id) || agents[0] || null;
  const agentName = agent?.name || t("handle.theAgent");

  const resolve = async (resolution: Outcome, callbackAt?: number) => {
    try {
      await handleMessage.mutateAsync({
        id: message.id,
        resolution,
        callback_at: callbackAt,
      });
      toast.success(
        resolution === "delegated" ? t("handle.doneWithCallback") : t("handle.done"),
      );
      onDone?.("resolved");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("handle.failed"));
    }
  };

  /** Why the AI cannot call back, if it cannot. */
  const unavailable = !message.phone_number
    ? t("handle.callbackNoPhone")
    : slot?.enabled === false
      ? t("handle.callbackDisabled")
      : !text
        ? t("handle.nothingToTell")
        : null;

  const callbackLater = slot?.at && slot.at > Date.now() ? slot.at : 0;

  return (
    // Not modal: in the dialog, the dialog already shuts the page out. A modal
    // menu registers itself as a layer blocking outside clicks until its exit
    // animation ends, and an item here closes the dialog — unmounted inside that
    // window (a fast answer, or the row leaving the pending list), the dialog sees
    // another blocking layer, leaves `pointer-events: none` on <body>, and nothing
    // in the app is clickable any more.
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button type="button" size="sm" disabled={handleMessage.isPending}>
          {t("handle.resolve")}
          <CaretDownIcon className="ml-1.5 h-4 w-4" weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <Outcome
          label={t("handle.outcomes.callbackNow", { agent: agentName })}
          hint={unavailable}
          disabled={!callbackEnabled || !text}
          onSelect={() =>
            resolve("delegated", Date.now() + CALLBACK_NOW_DELAY_MS)
          }
        />
        <Outcome
          label={t("handle.outcomes.callbackLater", { agent: agentName })}
          hint={
            unavailable ??
            (slotLoading
              ? t("handle.callbackLoading")
              : callbackLater
                ? t("handle.outcomes.plannedFor", {
                    date: format(callbackLater, "Pp"),
                  })
                : null)
          }
          disabled={!callbackEnabled || !text || !callbackLater}
          onSelect={() => resolve("delegated", callbackLater)}
        />
        <DropdownMenuSeparator />
        <Outcome
          label={t("handle.outcomes.handledByHuman")}
          hint={t("handle.outcomes.handledByHumanHint")}
          onSelect={() => resolve("handled_by_human")}
        />
        <Outcome
          label={t("handle.outcomes.dropped")}
          hint={t("handle.outcomes.droppedHint")}
          onSelect={() => resolve("dropped")}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Outcome({
  label,
  hint,
  disabled,
  onSelect,
}: {
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem onClick={onSelect} disabled={disabled}>
      <div className="space-y-0.5">
        <div>{label}</div>
        {hint && <div className="text-muted-foreground text-xs">{hint}</div>}
      </div>
    </DropdownMenuItem>
  );
}
